import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { NavItemId, NavSectionId } from "./constants";
import { NavCustomizationContext } from "./use-nav-customization";
import {
  DEFAULT_NAV_SETTINGS,
  isDefaultNavLayout,
  NavSettings,
  navSettingsEqual,
  resolveNavSections,
  ResolvedNavSection,
  serializeNavSections,
  updateNavSettings
} from "./nav-settings";
import { usePermittedNavSections } from "./use-permitted-nav-sections";

const SAVE_DEBOUNCE_MS = 400;
const SAVED_STATUS_MS = 1800;
const UNDO_TOAST_AUTO_CLOSE_MS = 6000;

const UndoResetToastContent = ({
  onUndo,
  closeToast
}: {
  onUndo: () => void;
  closeToast: () => void;
}) => (
  <div className="flex w-full items-center justify-between gap-3">
    <span className="text-sm text-white">Menu reset to default</span>
    <Button
      size={ButtonSize.XS}
      variant={ButtonVariant.BASE}
      onClick={() => {
        onUndo();
        closeToast();
      }}
    >
      Undo
    </Button>
  </div>
);

const moveWithin = (
  sections: ResolvedNavSection[],
  sectionId: NavSectionId,
  from: number,
  to: number
) =>
  sections.map((section) => {
    if (section.id !== sectionId) return section;

    const items = [...section.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    return { ...section, items };
  });

const toggleWithin = (sections: ResolvedNavSection[], id: NavItemId) =>
  sections.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.id === id ? { ...item, isHidden: !item.isHidden } : item
    )
  }));

export const NavCustomizationProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { auth } = useAuthContext();
  const permittedSections = usePermittedNavSections();

  const [settings, setSettings] = useState<NavSettings>(
    () => auth?.ui_settings?.nav ?? DEFAULT_NAV_SETTINGS
  );
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const latestRef = useRef(settings);
  const lastSavedRef = useRef(settings);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const inFlightRef = useRef(false);
  const queuedRef = useRef(false);

  const { mutate } = useMutation({
    mutationKey: ["NAV_SETTINGS", "UPDATE"],
    mutationFn: updateNavSettings
  });

  const persistRef = useRef<() => void>(() => {});
  persistRef.current = () => {
    if (inFlightRef.current) {
      queuedRef.current = true;
      return;
    }

    const next = latestRef.current;
    if (navSettingsEqual(next, lastSavedRef.current)) return;

    inFlightRef.current = true;
    mutate(next, {
      onSuccess: () => {
        lastSavedRef.current = next;
        setIsSaved(true);
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(
          () => setIsSaved(false),
          SAVED_STATUS_MS
        );
      },
      onError: (error) => {
        if (navSettingsEqual(latestRef.current, next)) {
          latestRef.current = lastSavedRef.current;
          setSettings(lastSavedRef.current);
        }
        toast.error(`Failed to save menu: ${getErrorMessage(error)}`);
      },
      onSettled: () => {
        inFlightRef.current = false;
        if (queuedRef.current) {
          queuedRef.current = false;
          persistRef.current();
        }
      }
    });
  };

  const flush = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    persistRef.current();
  }, []);

  const commit = useCallback(
    (next: NavSettings, { immediate = false } = {}) => {
      latestRef.current = next;
      setSettings(next);
      clearTimeout(saveTimerRef.current);
      if (immediate) persistRef.current();
      else
        saveTimerRef.current = setTimeout(
          () => persistRef.current(),
          SAVE_DEBOUNCE_MS
        );
    },
    []
  );

  useEffect(
    () => () => {
      clearTimeout(saveTimerRef.current);
      clearTimeout(savedTimerRef.current);
    },
    []
  );

  const authUserId = auth?.id;
  const authNavSettings = auth?.ui_settings?.nav;
  const authNavSettingsRef = useRef(authNavSettings);
  authNavSettingsRef.current = authNavSettings;

  useEffect(() => {
    const next = authNavSettingsRef.current ?? DEFAULT_NAV_SETTINGS;
    clearTimeout(saveTimerRef.current);
    queuedRef.current = false;
    latestRef.current = next;
    lastSavedRef.current = next;
    setSettings(next);
    setIsCustomizing(false);
  }, [authUserId]);

  const sections = useMemo(
    () => resolveNavSections(permittedSections, settings),
    [permittedSections, settings]
  );

  const isDefault = useMemo(
    () => isDefaultNavLayout(permittedSections, sections),
    [permittedSections, sections]
  );

  const toggleHidden = useCallback(
    (id: NavItemId) => commit(serializeNavSections(toggleWithin(sections, id))),
    [commit, sections]
  );

  const moveItem = useCallback(
    (sectionId: NavSectionId, from: number, to: number) =>
      commit(serializeNavSections(moveWithin(sections, sectionId, from, to))),
    [commit, sections]
  );

  const reset = useCallback(() => {
    const previous = latestRef.current;
    commit(DEFAULT_NAV_SETTINGS, { immediate: true });

    let undone = false;
    toast.success(
      ({ closeToast }) => (
        <UndoResetToastContent
          closeToast={closeToast}
          onUndo={() => {
            if (undone) return;
            undone = true;
            commit(previous, { immediate: true });
          }}
        />
      ),
      { autoClose: UNDO_TOAST_AUTO_CLOSE_MS, closeOnClick: false }
    );
  }, [commit]);

  const startCustomizing = useCallback(() => setIsCustomizing(true), []);

  const stopCustomizing = useCallback(() => {
    setIsCustomizing(false);
    flush();
  }, [flush]);

  useEffect(() => {
    if (!isCustomizing) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) stopCustomizing();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isCustomizing, stopCustomizing]);

  const value = useMemo(
    () => ({
      sections,
      isCustomizing,
      isDefault,
      isSaved,
      startCustomizing,
      stopCustomizing,
      toggleHidden,
      moveItem,
      reset
    }),
    [
      sections,
      isCustomizing,
      isDefault,
      isSaved,
      startCustomizing,
      stopCustomizing,
      toggleHidden,
      moveItem,
      reset
    ]
  );

  return (
    <NavCustomizationContext.Provider value={value}>
      {children}
    </NavCustomizationContext.Provider>
  );
};
